import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Select, Switch, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import ToggleDays from './ToggleDays';

enum DurationMode {
  Weeks = 'weeks',
  EndDate = 'end_date',
}

const WEEK_OPTIONS = [1, 2, 3, 4, 6, 8, 12];
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_HEADER_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DEFAULT_DAYS = [0, 1, 2, 3, 4];

export type RecurringScheduleValue = {
  enabled: boolean;
  days: number[];
  end_date: string;
};

type RecurringScheduleConfigProps = {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  startDate: moment.Moment;
  onChange: (value: RecurringScheduleValue | undefined) => void;
  label?: string;
  activityLabelSingular?: string;
  activityLabelPlural?: string;
  initialDays?: number[];
  initialWeeks?: number;
};

type MonthGridProps = {
  year: number;
  month: number;
  highlightedDates: Set<string>;
  rangeStart: moment.Moment;
  rangeEnd: moment.Moment;
};

const MonthGrid = ({ year, month, highlightedDates, rangeStart, rangeEnd }: MonthGridProps) => {
  const { t } = useTranslation();
  const firstDay = moment({ year, month, day: 1 });
  const daysInMonth = firstDay.daysInMonth();
  const startDayOfWeek = firstDay.day();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    cells.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(d);
  }

  return (
    <Grid container flexDirection="column" sx={{ width: 192 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center', marginBottom: 0.5 }}>
        {firstDay.format('MMMM YYYY')}
      </Typography>
      <Grid container gap={0.5}>
        {DAY_HEADER_KEYS.map((key, i) => (
          <Grid
            item
            key={i}
            sx={{
              width: 24,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {t(`days.${key}`)}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Grid container gap={0.5}>
        {cells.map((day, i) => {
          if (day === null) {
            return <Grid item key={`empty-${i}`} sx={{ width: 24, height: 24 }} />;
          }
          const date = moment({ year, month, day });
          const dateKey = date.format('YYYY-MM-DD');
          const isHighlighted = highlightedDates.has(dateKey);
          const isInRange = date.isSameOrAfter(rangeStart, 'day') && date.isSameOrBefore(rangeEnd, 'day');

          return (
            <Grid
              item
              key={day}
              sx={{
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                backgroundColor: isHighlighted ? 'primary.main' : isInRange ? 'mb.ground' : 'transparent',
                color: isHighlighted ? 'primary.contrastText' : 'text.disabled',
                textDecoration: isInRange && !isHighlighted ? 'line-through' : 'none',
                fontSize: '0.7rem',
              }}>
              {day}
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};

export const RecurringScheduleConfig = ({
  enabled,
  onEnabledChange,
  startDate,
  onChange,
  label,
  activityLabelSingular,
  activityLabelPlural,
  initialDays = DEFAULT_DAYS,
  initialWeeks = 1,
}: RecurringScheduleConfigProps) => {
  const { t } = useTranslation();
  const [days, setDays] = useState<number[]>(initialDays);
  const [weeks, setWeeks] = useState(initialWeeks);
  const [durationMode, setDurationMode] = useState<DurationMode>(DurationMode.Weeks);
  const [customEndDate, setCustomEndDate] = useState<moment.Moment>(() =>
    moment(startDate.format('YYYY-MM-DD')).add(1, 'week'),
  );

  const checkboxLabel = label || t('recurring_schedule.repeating');
  // Stabilize startDate by its string value so that callers passing
  // `moment(values.date)` on every render don't trigger infinite loops.
  const startDateStr = startDate.format('YYYY-MM-DD');
  const endDate = useMemo(
    () => (durationMode === DurationMode.Weeks ? moment(startDateStr).add(weeks, 'weeks') : customEndDate),
    [durationMode, startDateStr, weeks, customEndDate],
  );

  useEffect(() => {
    setCustomEndDate(moment(startDateStr).add(1, 'week'));
  }, [startDateStr]);

  // Use a ref for onChange to avoid infinite re-render loops when the parent
  // passes an inline callback (e.g. inside Formik render props).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleModeChange = useCallback(
    (mode: DurationMode) => {
      setDurationMode(mode);
      setWeeks(initialWeeks);
      setCustomEndDate(moment(startDateStr).add(1, 'week'));
    },
    [initialWeeks, startDateStr],
  );

  const notifyChange = useCallback(() => {
    if (!enabled) {
      onChangeRef.current(undefined);
      return;
    }
    onChangeRef.current({
      enabled: true,
      days,
      end_date: endDate.format('YYYY-MM-DD'),
    });
  }, [enabled, days, endDate]);

  useEffect(() => {
    notifyChange();
  }, [notifyChange]);

  const { highlightedDates, occurrenceCount } = useMemo(() => {
    const dates = new Set<string>();
    if (!enabled || days.length === 0) return { highlightedDates: dates, occurrenceCount: 0 };
    const current = moment(startDateStr).startOf('day');
    const end = moment(endDate).endOf('day');
    while (current.isSameOrBefore(end)) {
      if (days.includes(current.day())) {
        dates.add(current.format('YYYY-MM-DD'));
      }
      current.add(1, 'day');
    }
    return { highlightedDates: dates, occurrenceCount: dates.size };
  }, [enabled, days, startDateStr, endDate]);

  const monthsToShow = useMemo(() => {
    if (!enabled) return [];
    const start = moment(startDateStr);
    const end = moment(endDate);
    const months: { year: number; month: number }[] = [];
    const current = moment(start).startOf('month');
    while (current.isSameOrBefore(end, 'month')) {
      months.push({ year: current.year(), month: current.month() });
      current.add(1, 'month');
    }
    return months;
  }, [enabled, startDateStr, endDate]);

  const selectedDaySummary = useMemo(() => {
    if (days.length === 0) return '';
    return days
      .slice()
      .sort((a, b) => a - b)
      .map((d) => t(`days.${DAY_KEYS[d]}`))
      .join(', ');
  }, [days, t]);

  return (
    <Grid container flexDirection="column">
      <Grid item mb={enabled ? 1 : undefined}>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => onEnabledChange(e.target.checked)} />}
          label={<Typography variant="body2">{checkboxLabel}</Typography>}
        />
      </Grid>

      {enabled && (
        <Grid
          container
          flexDirection="column"
          gap={2}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', padding: 2 }}>
          <Grid container alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('recurring_schedule.in_days')}
            </Typography>
            <Grid item>
              <ToggleDays days={days} setDays={setDays} dimUnselected />
            </Grid>
          </Grid>

          {days.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('recurring_schedule.selected_days')} {selectedDaySummary}
            </Typography>
          ) : (
            <Typography variant="body2" color="error">
              {t('recurring_schedule.no_days_selected')}
            </Typography>
          )}

          <Grid container flexDirection="column" gap={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('recurring_schedule.repeat_for')}
            </Typography>
            <RadioGroup value={durationMode} onChange={(e) => handleModeChange(e.target.value as DurationMode)}>
              <Grid container flexDirection="column" gap={1.5}>
                <Grid container alignItems="center" gap={1}>
                  <FormControlLabel
                    value={DurationMode.Weeks}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{t('recurring_schedule.for')}</Typography>}
                    sx={{ marginInlineEnd: 0 }}
                  />
                  <Select
                    size="small"
                    value={weeks}
                    disabled={durationMode !== DurationMode.Weeks}
                    onChange={(e) => setWeeks(e.target.value as number)}
                    sx={{ minWidth: 120 }}>
                    {WEEK_OPTIONS.map((w) => (
                      <MenuItem key={w} value={w}>
                        {t('recurring_schedule.weeks', { count: w })}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid container alignItems="center" gap={1}>
                  <FormControlLabel
                    value={DurationMode.EndDate}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{t('recurring_schedule.until')}</Typography>}
                    sx={{ marginInlineEnd: 0 }}
                  />
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      disabled={durationMode !== DurationMode.EndDate}
                      value={durationMode === DurationMode.EndDate ? customEndDate : null}
                      minDate={moment(startDateStr).add(1, 'day')}
                      onChange={(date) => {
                        if (date) setCustomEndDate(date);
                      }}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </RadioGroup>
          </Grid>

          {occurrenceCount > 0 && (
            <Typography variant="body1" sx={{ fontWeight: 700, textAlign: 'center' }}>
              {`${occurrenceCount} ${
                occurrenceCount === 1
                  ? activityLabelSingular || t('recurring_schedule.activity')
                  : activityLabelPlural || t('recurring_schedule.activities')
              } - ${startDate.format('D MMM')} - ${endDate.format('D MMM')}`}
            </Typography>
          )}

          <Grid container gap={2} sx={{ justifyContent: 'center' }}>
            {monthsToShow.map(({ year, month }) => (
              <MonthGrid
                key={`${year}-${month}`}
                year={year}
                month={month}
                highlightedDates={highlightedDates}
                rangeStart={startDate}
                rangeEnd={endDate}
              />
            ))}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
